import { useFragment } from '@apollo/client';
import { ItemFragmentFragment, UserFragmentFragment } from '@apps/www/src/__generated__/graphql';
import SVWithTooltipErrorBoundary from '@apps/www/src/www/containers/SVWithTooltipErrorBoundary';
import useAllowedBoardsToSave from '@apps/www/src/www/hooks/useAllowedBoardsToSave';
import useAllowedFeatureCheckEvent from '@apps/www/src/www/hooks/useAllowedFeatureCheckEvent';
import useIsLoggedIn from '@apps/www/src/www/hooks/useIsLoggedIn';
import useSaveItem from '@apps/www/src/www/hooks/useSaveItem';
import SVNewBoardModal from '@apps/www/src/www/modals/SVNewBoardModal';
import ItemFragment from '@apps/www/src/www/queries/fragments/ItemFragment';
import { toggleItemSort } from '@apps/www/src/www/reducers/grid';
import SVButton from '@pkgs/shared-client/components/SVButton';
import SVDropdown from '@pkgs/shared-client/components/SVDropdown';
import SVDropdownContent from '@pkgs/shared-client/components/SVDropdownContent';
import SVIconButton from '@pkgs/shared-client/components/SVIconButton';
import SVModal from '@pkgs/shared-client/components/SVModal';
import cn from '@pkgs/shared-client/helpers/cn';
import { emptyPreventDefault } from '@pkgs/shared-client/helpers/dom';
import {
	addLongPressListener,
	removeLongPressListener,
} from '@pkgs/shared-client/helpers/longPress';
import plural from '@pkgs/shared-client/helpers/plural';
import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import IconChevronDownSVG from '@pkgs/shared-client/img/icon-chevron-down-inlined.svg';
import IconMoveSVG from '@pkgs/shared-client/img/icon-move-two-lines-inlined.svg';
import { type GridHoverOverlayUISize } from '@pkgs/shared/constants';
import AllowedFeature from '@pkgs/shared/enums/AllowedFeature';
import BoardOwnershipType from '@pkgs/shared/enums/BoardOwnershipType';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

const UI_SIZE_CLASSES = {
	large: {
		padding: 'p-[14px]',
		font: 'text-base',
		icon: 'w-[24px] h-[24px] min-w-[24px] min-h-[24px]',
		hoverIcon: 'w-[32px] h-[32px] min-w-[32px] min-h-[32px]',
	},
	medium: {
		padding: 'p-3',
		font: 'text-sm',
		icon: 'w-[20px] h-[20px] min-w-[20px] min-h-[20px]',
		hoverIcon: 'w-[28px] h-[28px] min-w-[28px] min-h-[28px]',
	},
	small: {
		padding: 'p-[10px]',
		font: 'text-xs',
		icon: 'w-[18px] h-[18px] min-w-[18px] min-h-[18px]',
		hoverIcon: 'w-[26px] h-[26px] min-w-[26px] min-h-[26px]',
	},
};

const _MoveButton = ({
	item,
	isSortLoading,
	uiSize,
}: {
	item: ItemFragmentFragment;
	isSortLoading: boolean;
	uiSize: GridHoverOverlayUISize;
}) => {
	const dispatch = useDispatch();

	const handleItemMove = useEventCallback(() => {
		dispatch(toggleItemSort(item._id, true));
	});

	return (
		<SVIconButton
			className={cn(
				'duration-over cursor-grab transition-opacity ease-out',
				isSortLoading && 'pointer-events-none opacity-50',
			)}
			iconClassName={cn('text-white', UI_SIZE_CLASSES[uiSize].icon)}
			src={IconMoveSVG}
			onMouseDown={handleItemMove}
			onGrid={UI_SIZE_CLASSES[uiSize].hoverIcon}
		/>
	);
};

const _SaveButton = ({
	items,
	uiSize,
	showTooltipError,
	onDropdownOpenChange,
}: {
	items: ItemFragmentFragment[];
	uiSize: GridHoverOverlayUISize;
	showTooltipError: (error: Error | undefined) => void;
	onDropdownOpenChange?: (isOpen: boolean) => void;
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const { isSaved, isLoading, itemsCount, toggleSave } = useSaveItem({
		items,
		showTooltipError,
	});
	const isLoggedIn = useIsLoggedIn();
	const boards = useAllowedBoardsToSave(items);

	const handleClick = useEventCallback((event: React.UIEvent) => {
		toggleSave();

		if (event) {
			event.preventDefault();
			event.stopPropagation();
			return false;
		}
	});

	const handleLongPress = useEventCallback(() => {
		handleDropdownChange(true);
	});

	const setupLongPress = useEventCallback((ref: HTMLElement) => {
		if (ref) {
			addLongPressListener(ref, handleLongPress);
			return () => {
				removeLongPressListener(ref);
			};
		}
	});

	const handleDropdownChange = useEventCallback((isOpen: boolean) => {
		setIsOpen(isOpen);
		onDropdownOpenChange?.(isOpen);
	});

	const handleSaveToBoard = useEventCallback(
		(board: ArrayElement<UserFragmentFragment['boards']>) => {
			toggleSave(board._id);
		},
	);

	const handleSaveToNewBoard = useAllowedFeatureCheckEvent(AllowedFeature.ADD_BOARD, () => {
		SVModal.open(SVNewBoardModal, {
			items,
			ownershipType: BoardOwnershipType.USER,
			autoRedirect: true,
		});
	});

	if (!isLoggedIn) {
		return null;
	}

	return (
		<SVDropdown
			triggerType={SVDropdown.TRIGGER_TYPES.NONE}
			isOpen={isOpen}
			onClose={() => handleDropdownChange(false)}
			contentOffset="h-1"
			keepOpenOnClick
			renderTrigger={({ isOpen: dropdownOpen, ref, ...props }) => {
				return (
					<SVButton
						ref={(el) => {
							ref(el);
							if (el) setupLongPress(el);
						}}
						onClick={handleClick}
						isLoading={isLoading}
						title={plural(itemsCount, 'Save')}
						className={cn(
							'duration-over relative flex w-fit rounded-full px-3 py-2 font-medium transition-all',
							!isSaved
								? 'bg-white text-black hover:bg-white hover:opacity-80'
								: 'bg-gray-900 bg-opacity-30 text-white backdrop-blur-lg hover:bg-gray-900 hover:bg-opacity-40',
							UI_SIZE_CLASSES[uiSize].font,
						)}
						{...props}
					>
						<div>{isSaved ? 'Saved' : 'Save'}</div>
						{isSaved ? (
							<>
								<div className="flex items-center justify-center">
									<div className="ml-3 mr-2 h-[16px] w-[1px] bg-gray-200 bg-opacity-20" />
									<div className="flex-1" onClick={emptyPreventDefault}>
										<SVIconButton
											src={IconChevronDownSVG}
											onClick={() => handleDropdownChange(!isOpen)}
											iconClassName="h-[14px] w-[14px] min-h-[14px] min-w-[14px]"
											disableHover
										/>
									</div>
								</div>
							</>
						) : null}
					</SVButton>
				);
			}}
			renderContent={() => (
				<SVDropdownContent.Boards
					boards={boards}
					onNewBoard={handleSaveToNewBoard}
					onBoardClick={handleSaveToBoard}
					selectedIDs={items.flatMap((item) =>
						item.asset.ownBoards.map((board) => board._id),
					)}
				/>
			)}
			maxHeight={346}
		/>
	);
};

const _SaveButtonContainer = SVWithTooltipErrorBoundary(_SaveButton);

const _Content = ({
	canMove,
	item,
	items,
	isSortLoading,
	uiSize,
	onDropdownOpenChange,
}: {
	canMove: boolean;
	item: ItemFragmentFragment;
	items: ItemFragmentFragment[];
	isSortLoading: boolean;
	uiSize: GridHoverOverlayUISize;
	onDropdownOpenChange?: (isOpen: boolean) => void;
}) => {
	return (
		<>
			<div className="absolute h-full w-full bg-black bg-opacity-15" />
			<div className={cn('absolute flex h-full w-full', UI_SIZE_CLASSES[uiSize].padding)}>
				<div className="relative z-50 flex-1">
					<div className="flex h-min w-full items-center justify-between">
						{canMove ? (
							<div onClick={emptyPreventDefault}>
								<_MoveButton
									item={item}
									isSortLoading={isSortLoading}
									uiSize={uiSize}
								/>
							</div>
						) : (
							<div />
						)}
						<div onClick={emptyPreventDefault}>
							<_SaveButtonContainer
								items={items}
								uiSize={uiSize}
								onDropdownOpenChange={onDropdownOpenChange}
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

const SVGridItemHoverOverlayContainer = ({
	item: itemProp,
	uiSize,
	isSortLoading,
	canMove,
	onDropdownOpenChange,
}: {
	item: ItemFragmentFragment;
	uiSize: GridHoverOverlayUISize;
	isSortLoading: boolean;
	canMove: boolean;
	onDropdownOpenChange?: (isOpen: boolean) => void;
}) => {
	// TODO: Can we remove this and adjust on SVBaseItemsContainer to always re-reference the renderHoverOverlay prop whenever `item` changes?
	// We watch the fragment here to get updates from apollo cache, otherwise the `SVGridItem` won't send updates
	// whenever `asset.ownBoards` changes, for example.
	const { data: itemFragment, complete } = useFragment<ItemFragmentFragment>({
		fragment: ItemFragment,
		from: {
			__typename: 'Item',
			_id: itemProp._id,
		},
	});

	const item = complete ? itemFragment : itemProp;

	const items = useMemo(() => [item], [item]);

	return (
		<div className="grid-hide-on-editing theme-dark absolute left-0 top-0 h-full w-full">
			<_Content
				canMove={canMove}
				item={item}
				items={items}
				isSortLoading={isSortLoading}
				uiSize={uiSize}
				onDropdownOpenChange={onDropdownOpenChange}
			/>
		</div>
	);
};

export default SVGridItemHoverOverlayContainer;
